import axios from 'axios';
import { toast } from 'react-toastify';
import * as Sentry from '@sentry/react';
import { authService } from '@features/auth/services/authService';

/**
 * Fetch a guest-portal confirmation URL for a mobile-service stop.
 *
 * The URL is a stateless magic link (the hash encodes the stop id), generated
 * server-side by generateGuestLink because the encryption key never ships to
 * the browser. Requesting a link also refreshes the stop's 72h confirmation
 * window (service_stop_details.hash_expires_at).
 */
export async function fetchServiceStopConfirmLink(stopId: number): Promise<string | null> {
  const token = await authService.getAccessTokenWithAutoRefresh();
  if (!token) {
    toast.error('You must be signed in to generate a confirmation link');
    return null;
  }

  const { data } = await axios.post(
    '/.netlify/functions/generateGuestLink',
    {
      guest_id: stopId,
      id_type: 'service_stop',
    },
    {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    }
  );

  if (data?.success && data?.url) {
    return data.url as string;
  }
  return null;
}

/**
 * Re-send the confirmation-request SMS for a stop and refresh its 72h link
 * window. The server queues the message through the shared smsmessages →
 * db-smsmessages pipeline (same template as the dispatch-time send).
 * Returns true when the SMS was queued.
 */
export async function resendServiceStopConfirmationSMS(stopId: number): Promise<boolean> {
  const token = await authService.getAccessTokenWithAutoRefresh();
  if (!token) {
    toast.error('You must be signed in to resend a confirmation link');
    return false;
  }

  try {
    const { data } = await axios.post(
      '/.netlify/functions/resendConfirmationLink',
      { stop_id: stopId },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      }
    );
    if (data?.success) {
      toast.success('Confirmation link texted to the customer');
      return true;
    }
    toast.error('Failed to resend the confirmation link');
    return false;
  } catch (err) {
    const serverError = axios.isAxiosError(err) ? err.response?.data?.error : null;
    console.error('Failed to resend confirmation link:', err);
    Sentry.captureException(err, {
      tags: { feature: 'mobile-service', action: 'resend-confirm-link' },
      extra: { stopId },
    });
    toast.error(typeof serverError === 'string' ? serverError : 'Failed to resend the confirmation link');
    return false;
  }
}
